/**
 * WizardReturnBanner — slim reminder shown on the Leads, Find Leads and
 * Settings pages while the user is mid-detour from the campaign wizard ("Add
 * leads" in the Leads step, "Edit in Settings" on the Offer row and the
 * Settings > Claude link on the Method step set dca_wizard_return before
 * navigating here).
 *
 * "Back to campaign setup" reuses the existing dca_open_wizard hand-off
 * (campaigns-list.tsx reads it once and auto-opens the wizard), and the
 * wizard restores its saved draft on open — so the user lands exactly where
 * they left off, with any freshly imported leads visible in the picker.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { clearWizardReturn, hasWizardReturn, requestWizardOpen } from './wizard-draft'

export function WizardReturnBanner({
  message = 'You’re adding leads for a campaign you’re still setting up.',
}: {
  /** What the detour is for (Settings uses its own line). */
  readonly message?: string
} = {}) {
  const navigate = useNavigate()
  // Read once on mount — the flag only changes through this banner's own
  // buttons, so there's nothing to subscribe to.
  const [visible, setVisible] = useState(() => hasWizardReturn())

  if (!visible) return null

  function handleReturn() {
    clearWizardReturn()
    requestWizardOpen()
    navigate('/campaigns')
  }

  function handleDismiss() {
    // The user decided to stay — drop the flag so the banner doesn't chase
    // them around. The wizard draft itself survives; reopening the wizard
    // from Campaigns still restores it.
    clearWizardReturn()
    setVisible(false)
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--color-cobalt)]/30 bg-[var(--color-cobalt)]/5 px-4 py-2">
      <p className="text-sm">{message}</p>
      <div className="flex items-center gap-1">
        <Button size="sm" className="h-7 text-xs" onClick={handleReturn}>
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to campaign setup
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0"
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}
